<template>
  <div
    class="d-flex justify-space-between q-row__block"
    :class="{
      'error--block': errors.length,
      'q-row__block--active': focusInput === aiQuestion.id,
    }"
  >
    <div class="q-row__content">
      <span
        class="q-row__index"
        :class="{
          'error--text': errors.length,
          'exceed-limit': exceedLimitQuestion
        }"
      >
        {{ (index + 1) }}.
      </span>
      <VTextarea
        v-if="focusInput === aiQuestion.id"
        v-model="aiQuestion.text"
        class="q-row__text"
        :class="{ 'exceed-limit': exceedLimitQuestion }"
        rows="1"
        maxlength="1500"
        loading="false"
        auto-grow
        @blur="focusInput !== aiQuestion.id"
        @input="hideError"
        @keydown.esc="confirmAiQuestionChanges"
        @keydown.enter.prevent="confirmAiQuestionChanges"
        :counter="1500"
        ref="inputRef"
        :error-messages="errors"
        :placeholder="$t('enterQuestion')"
      />
      <VTextarea
        v-else
        readonly
        v-model="aiQuestion.text"
        rows="1"
        class="q-row__text q-row__text--default"
        :class="{ 'exceed-limit': exceedLimitQuestion }"
        loading="false"
        auto-grow
        ref="inputRef"
        :error-messages="errors"
        :placeholder="$t('enterQuestion')"
      />
    </div>
    <VCardActions>
      <VBtn
        v-if="focusInput !== aiQuestion.id"
        class="q-row__btn"
        :class="{ 'exceed-limit': exceedLimitQuestion }"
        small
        icon
        :disabled="disabled"
        @click="setFocusInput"
      >
        <VIcon :class="{ 'exceed-limit': exceedLimitQuestion }">
          mdi-pencil
        </VIcon>
      </VBtn>
      <VBtn
        v-else
        class="q-row__btn"
        :class="{ 'exceed-limit': exceedLimitQuestion }"
        small
        icon
        :disabled="disabled"
        @click="confirmAiQuestionChanges"
      >
        <VIcon :class="{ 'exceed-limit': exceedLimitQuestion }">
          mdi-check
        </VIcon>
      </VBtn>
      <VBtn
        class="q-row__btn"
        :class="{ 'exceed-limit': exceedLimitQuestion }"
        small
        icon
        :disabled="disabled"
        @click="$emit('showDestroyDialog', aiQuestion)"
      >
        <VIcon :class="{ 'exceed-limit': exceedLimitQuestion }">
          mdi-trash-can-outline
        </VIcon>
      </VBtn>
    </VCardActions>
  </div>
</template>

<script>
import {
  toRefs, ref, nextTick, computed,
} from '@vue/composition-api';
import { useI18n } from 'vue-i18n-composable';

export default {
  name: 'VacancyAiQuestionRow',
  props: {
    aiQuestion: {
      type: Object,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
    focusInput: {
      type: Number,
      required: true,
    },
    disabled: {
      type: Boolean,
      required: true,
    },
    exceedLimitQuestion: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  setup(props, { emit }) {
    const { aiQuestion } = toRefs(props);
    const { t } = useI18n();

    const inputRef = ref(null);
    const errors = ref([]);

    const isEmptyAiQuestion = computed(() => aiQuestion.value.text.replace(/\s/g, '').length <= 0);

    function setFocusInput() {
      emit('setFocusInput', aiQuestion.value.id);
      nextTick(() => {
        inputRef.value?.focus();
      });
    }

    function hideError() {
      if (errors.value.length) {
        errors.value = [];
      }
    }

    function confirmAiQuestionChanges() {
      if (isEmptyAiQuestion.value) {
        errors.value.push(t('requiredField'));
        return;
      }
      emit('confirmAiQuestionChanges');
    }

    return {
      setFocusInput,
      confirmAiQuestionChanges,
      inputRef,
      hideError,
      errors,
    };
  },
};
</script>

<style lang="scss" scoped>
.q-row {
  &__block {
    width: 100%;
    height: fit-content;
    &--active {
      height: 100%;
    }
  }
  &__content {
    display: flex;
    align-items: center;
    width: 90%;
  }
  &__index {
    border-right: 8px dotted #fafafa;
    color: #757575;
    text-align: center;
    font-size: 20px;
    font-weight: 400;
    line-height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    max-width: 75px;
    width: 100%;
    height: 100%;
    @media screen and (max-width: 460px) {
      font-size: 15px;
    }
  }
  &__text {
    padding: 2px 10px;
    margin: 0;
    outline: none;
    border: none;
    resize: none;
    overflow: hidden;
    @media screen and (max-width: 460px) {
      font-size: 12px;
    }
    &--default {
      pointer-events: none;
      user-select: none;
    }
  }
  &__text-active {
    border: 1px solid #0a82d9;
  }
  &__btn {
    margin-right: 25px;
    @media screen and (max-width: 460px) {
      margin-right: 0;
    }
  }
}

.v-card__actions {
  @media screen and (max-width: 400px) {
    padding: 0;
  }
}

::v-deep {
  .v-input__slot {
    margin-bottom: 0;
    .v-text-field__slot textarea {
      padding-bottom: 5px !important;
    }
  }
  .exceed-limit .v-text-field__slot textarea {
    color: #ff5252 !important;
  }
}

.error--block {
  border: 1px solid #ff5252;
}

.exceed-limit {
  color: #ff5252 !important;
}
</style>

<i18n locale="en" lang="yaml">
  enterQuestion: Enter a question
  requiredField: Required field to fill in
</i18n>

<i18n locale="ru" lang="yaml">
  enterQuestion: Введите вопрос
  requiredField: Обязательное поле для заполнения
</i18n>
